import { normalizePostalCode } from '../../utils/postalCode';
import { DateTime } from 'luxon';
import {
  CleaningLevel,
  ServiceType,
  VehicleClass,
  WorkerSort,
  type ISearchWorkersInput,
  type IWorkerQuoteInput,
} from '../../../__generated__/graphql';

/**
 * Everything a services search is (spec 019 W10, W11), kept in the URL so a
 * link, a reload or a sign-in brings the same search back (D21). Pure.
 */

export type ServiceKindParam = 'home' | 'vehicle' | 'handover';

export const SERVICE_PARAM: Record<ServiceType, ServiceKindParam> = {
  [ServiceType.HomeTurnover]: 'home',
  [ServiceType.VehicleCleaning]: 'vehicle',
  [ServiceType.VehicleHandover]: 'handover',
};

const SERVICE_FROM_PARAM: Record<ServiceKindParam, ServiceType> = {
  home: ServiceType.HomeTurnover,
  vehicle: ServiceType.VehicleCleaning,
  handover: ServiceType.VehicleHandover,
};

export interface Bounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface ServiceSearchState {
  serviceType: ServiceType;
  /** What the Where box shows: a ZIP code, an address or "Map area". */
  where: string;
  zip?: string;
  lat?: number;
  lng?: number;
  bounds?: Bounds;
  /** One of the signed-in customer's homes or vehicles. */
  siteId?: string;
  /** yyyy-MM-dd in the site's zone. */
  date: string;
  /** HH:mm, 30-minute steps. */
  windowStart: string;
  windowEnd: string;
  bedrooms: number;
  bathrooms: number;
  sqft?: number;
  sameDayTurnover: boolean;
  vehicleClass: VehicleClass;
  cleaningLevel: CleaningLevel;
  /** D8: the most a handover may bill, in whole hours (default 3). */
  handoverHours: number;
  sort: WorkerSort;
  /** W27: the car trip a handover is for (`createJobIntent.linkedBookingId`). */
  linkedBookingId?: string;
}

export const DEFAULT_WINDOW_START = '10:00';
export const DEFAULT_WINDOW_END = '14:00';
export const DEFAULT_HANDOVER_HOURS = 3;
export const MAX_HANDOVER_HOURS = 12;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const TIME = /^([01]\d|2[0-3]):(00|30)$/;
/**
 * A US ZIP code or a Canadian postal code (spec 028), as the search sends it:
 * the 5 digits, or "M5V 3L9". Null when it is neither.
 */
export function searchPostalCode(text: string | null | undefined): string | null {
  const value = (text ?? '').trim();
  if (/^\d{5}$/.test(value)) return value;
  return normalizePostalCode('CA', value);
}

export function tomorrow(now: DateTime = DateTime.local()): string {
  return now.plus({ days: 1 }).toISODate()!;
}

export function defaultServiceSearch(now?: DateTime): ServiceSearchState {
  return {
    serviceType: ServiceType.HomeTurnover,
    where: '',
    date: tomorrow(now),
    windowStart: DEFAULT_WINDOW_START,
    windowEnd: DEFAULT_WINDOW_END,
    bedrooms: 1,
    bathrooms: 1,
    sameDayTurnover: false,
    vehicleClass: VehicleClass.Car,
    cleaningLevel: CleaningLevel.Standard,
    handoverHours: DEFAULT_HANDOVER_HOURS,
    sort: WorkerSort.Recommended,
  };
}

const num = (value: string | null): number | undefined => {
  if (value === null || value.trim() === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const oneOf = <T extends string>(value: string | null, allowed: readonly T[], fallback: T): T =>
  value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;

export function parseServiceSearch(params: URLSearchParams, now?: DateTime): ServiceSearchState {
  const base = defaultServiceSearch(now);
  const typeParam = params.get('type') as ServiceKindParam | null;
  const date = params.get('date');
  const from = params.get('from');
  const to = params.get('to');
  const zip = params.get('zip');
  const lat = num(params.get('lat'));
  const lng = num(params.get('lng'));
  const boundsParts = (params.get('bounds') ?? '').split(',').map(Number);
  const bounds =
    boundsParts.length === 4 && boundsParts.every(Number.isFinite)
      ? { north: boundsParts[0], south: boundsParts[1], east: boundsParts[2], west: boundsParts[3] }
      : undefined;
  const bedrooms = num(params.get('bd'));
  const bathrooms = num(params.get('ba'));
  const sqft = num(params.get('sqft'));
  const hours = num(params.get('hours'));
  return {
    ...base,
    serviceType:
      typeParam && SERVICE_FROM_PARAM[typeParam] ? SERVICE_FROM_PARAM[typeParam] : base.serviceType,
    where: params.get('where') ?? searchPostalCode(zip) ?? '',
    zip: searchPostalCode(zip) ?? undefined,
    lat: lat !== undefined && lng !== undefined ? lat : undefined,
    lng: lat !== undefined && lng !== undefined ? lng : undefined,
    bounds,
    siteId: params.get('site') || undefined,
    date: date && DAY.test(date) ? date : base.date,
    windowStart: from && TIME.test(from) ? from : base.windowStart,
    windowEnd: to && TIME.test(to) ? to : base.windowEnd,
    bedrooms:
      bedrooms !== undefined && Number.isInteger(bedrooms) && bedrooms >= 0 && bedrooms <= 10
        ? bedrooms
        : base.bedrooms,
    bathrooms:
      bathrooms !== undefined &&
      bathrooms >= 0.5 &&
      bathrooms <= 8 &&
      Number.isInteger(bathrooms * 2)
        ? bathrooms
        : base.bathrooms,
    sqft: sqft !== undefined && Number.isInteger(sqft) && sqft > 0 ? sqft : undefined,
    sameDayTurnover: params.get('sameDay') === '1',
    vehicleClass: oneOf(params.get('class'), Object.values(VehicleClass), base.vehicleClass),
    cleaningLevel: oneOf(params.get('level'), Object.values(CleaningLevel), base.cleaningLevel),
    handoverHours:
      hours !== undefined && Number.isInteger(hours) && hours >= 1 && hours <= MAX_HANDOVER_HOURS
        ? hours
        : base.handoverHours,
    sort: oneOf(params.get('sort'), Object.values(WorkerSort), base.sort),
    linkedBookingId: params.get('trip') || undefined,
  };
}

/** The URL of a search; only what differs from nothing is written. */
export function serviceSearchParams(state: ServiceSearchState): URLSearchParams {
  const p = new URLSearchParams();
  p.set('type', SERVICE_PARAM[state.serviceType]);
  if (state.where) p.set('where', state.where);
  if (state.zip) p.set('zip', state.zip);
  if (state.lat !== undefined && state.lng !== undefined) {
    p.set('lat', String(state.lat));
    p.set('lng', String(state.lng));
  }
  if (state.bounds) {
    const { north, south, east, west } = state.bounds;
    p.set('bounds', [north, south, east, west].map(n => n.toFixed(5)).join(','));
  }
  if (state.siteId) p.set('site', state.siteId);
  p.set('date', state.date);
  p.set('from', state.windowStart);
  p.set('to', state.windowEnd);
  if (state.serviceType === ServiceType.HomeTurnover) {
    p.set('bd', String(state.bedrooms));
    p.set('ba', String(state.bathrooms));
    if (state.sqft) p.set('sqft', String(state.sqft));
    if (state.sameDayTurnover) p.set('sameDay', '1');
  } else if (state.serviceType === ServiceType.VehicleCleaning) {
    p.set('class', state.vehicleClass);
    p.set('level', state.cleaningLevel);
  } else {
    p.set('hours', String(state.handoverHours));
  }
  if (state.sort !== WorkerSort.Recommended) p.set('sort', state.sort);
  if (state.linkedBookingId) p.set('trip', state.linkedBookingId);
  return p;
}

/** Where the search starts from, or null when the Where box is still empty. */
export function hasPlace(state: ServiceSearchState): boolean {
  return Boolean(
    state.siteId ||
    state.zip ||
    (state.lat !== undefined && state.lng !== undefined) ||
    state.bounds,
  );
}

/** Why the window cannot be searched, or null. */
export function windowProblem(state: Pick<ServiceSearchState, 'windowStart' | 'windowEnd'>) {
  if (!TIME.test(state.windowStart) || !TIME.test(state.windowEnd)) {
    return 'Pick times on the hour or half hour';
  }
  return state.windowEnd > state.windowStart ? null : 'The window must end after it starts';
}

function placeInput(state: ServiceSearchState) {
  if (state.siteId) return { siteId: state.siteId };
  if (state.bounds) return { bounds: state.bounds };
  if (state.zip) return { zip: state.zip };
  if (state.lat !== undefined && state.lng !== undefined) {
    return { center: { lat: state.lat, lng: state.lng } };
  }
  return null;
}

/** The `searchWorkers` input, or null until there is a place and a valid window. */
export function toSearchInput(state: ServiceSearchState, page = 1): ISearchWorkersInput | null {
  const place = placeInput(state);
  if (!place || windowProblem(state)) return null;
  const input: ISearchWorkersInput = {
    serviceType: state.serviceType,
    ...place,
    serviceDate: state.date,
    windowStart: state.windowStart,
    windowEnd: state.windowEnd,
    sort: state.sort,
    page,
    limit: 20,
  };
  if (state.serviceType === ServiceType.HomeTurnover && !state.siteId) {
    input.homeSize = {
      bedrooms: state.bedrooms,
      bathrooms: state.bathrooms,
      sqft: state.sqft ?? null,
      sameDayTurnover: state.sameDayTurnover,
    };
  }
  if (state.serviceType === ServiceType.HomeTurnover && state.siteId && state.sameDayTurnover) {
    input.homeSize = {
      bedrooms: state.bedrooms,
      bathrooms: state.bathrooms,
      sqft: state.sqft ?? null,
      sameDayTurnover: true,
    };
  }
  if (state.serviceType === ServiceType.VehicleCleaning) {
    input.vehicleClass = state.vehicleClass;
    input.cleaningLevel = state.cleaningLevel;
  }
  if (state.serviceType === ServiceType.VehicleHandover) {
    input.maxBillableMinutes = state.handoverHours * 60;
  }
  return input;
}

/** The place part of a `workerQuote` input (signed out: a ZIP, D21). */
export function quotePlace(
  state: ServiceSearchState,
): Pick<IWorkerQuoteInput, 'siteId' | 'zip'> | null {
  if (state.siteId) return { siteId: state.siteId };
  if (state.zip) return { zip: state.zip };
  return null;
}

/** 30-minute times of a day, `00:00` to `23:30`. */
export const WINDOW_TIMES: readonly string[] = Array.from({ length: 48 }, (_, i) => {
  const h = String(Math.floor(i / 2)).padStart(2, '0');
  return `${h}:${i % 2 ? '30' : '00'}`;
});

/** "10:00" → "10:00 AM". */
export function timeLabel(time: string): string {
  const [h, m] = time.split(':').map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** "Tue, Oct 20 · 10:00 AM to 2:00 PM". */
export function windowSummary(
  state: Pick<ServiceSearchState, 'date' | 'windowStart' | 'windowEnd'>,
) {
  const day = DateTime.fromISO(state.date);
  const dayText = day.isValid ? day.toFormat('ccc, LLL d') : state.date;
  return `${dayText} · ${timeLabel(state.windowStart)} to ${timeLabel(state.windowEnd)}`;
}

export const SEARCH_PATH = '/services/search';
export const searchPath = (state: ServiceSearchState) =>
  `${SEARCH_PATH}?${serviceSearchParams(state).toString()}`;
export const workerPath = (carId: string, state?: ServiceSearchState) =>
  `/services/workers/${carId}${state ? `?${serviceSearchParams(state).toString()}` : ''}`;
export const bookPath = (carId: string, state: ServiceSearchState) =>
  `/services/book/${carId}?${serviceSearchParams(state).toString()}`;
